import { TemplateChannelEnum, TemplateDirectionEnum, TemplateLocaleEnum } from '__generated__/types';
import { Merge } from '@xometry/ui';
import { FormInstance } from 'antd';
import { TemplateModalFormFieldEnum, TemplateModalFormValues } from 'components/TemplatesModal/TemplateModal.types';
import { TemplateCategoryEnum } from 'components/TemplatesPage/TemplatesManagerModal/TemplatesManagerModal.types';
import { useMemo } from 'react';
import { TemplateFragment } from 'utils/graphql/fragments/__generated__/template';
import { TemplateModalQuery } from 'utils/graphql/queries/__generated__/templateModal';

export type LocalesMap = Partial<
  Record<TemplateLocaleEnum, Merge<TemplateFragment, { direction: TemplateDirectionEnum }>>
>;

interface Args {
  form: FormInstance<TemplateModalFormValues>;
  templateModalData: TemplateModalQuery;
  selectedCategoryId: string | null;
  isVisibleToClient: boolean;
  isVisibleToPartner: boolean;
}

export const useTemplatesModalFormDerived = ({
  form,
  templateModalData,
  selectedCategoryId,
  isVisibleToClient,
  isVisibleToPartner,
}: Args) => {
  const category = useMemo(
    () => templateModalData.messengerTemplateCategories.find(({ id }) => selectedCategoryId === id),
    [selectedCategoryId, templateModalData.messengerTemplateCategories],
  );

  const isEmailSendingAvailable = Boolean(category?.channels.includes(TemplateChannelEnum.Email));
  const isUploadingFilesAvailable = !category?.channels.includes(TemplateChannelEnum.Whatsapp);

  const selectedConstantName = form.getFieldValue(TemplateModalFormFieldEnum.TemplateConstantName) as string;

  const selectedTemplateGroup = useMemo(() => {
    if (!category || !selectedConstantName) {
      return null;
    }

    return category.templatesGroups.find(({ constantName }) => constantName === selectedConstantName) ?? null;
  }, [category, selectedConstantName]);

  const actionableActions = useMemo(() => {
    const actions = selectedTemplateGroup?.messageActionsGroup?.actions ?? [];

    return [...actions].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [selectedTemplateGroup]);

  const hasActionableMessages = actionableActions.length > 0;

  const currentFormLocale = form.getFieldValue(TemplateModalFormFieldEnum.Locale) as TemplateLocaleEnum;

  const templateMapByConstantName = useMemo(() => {
    const templateMap: Record<string, LocalesMap> = {};

    category?.templatesGroups.forEach(({ constantName, name, templates, direction }) => {
      if (!constantName) {
        return;
      }

      templateMap[constantName] = templates.reduce<LocalesMap>(
        (acc, { id, locale }) => ({
          ...acc,
          [locale]: { constantName, name, locale, id, direction },
        }),
        {},
      );
    });

    return templateMap;
  }, [category?.templatesGroups]);

  const clientOrPartnerLocale = useMemo(() => {
    const related = templateModalData.messengerSourceRelatedObjects;

    if (isVisibleToClient) {
      return related?.person?.locale?.toLocaleUpperCase() ?? '';
    }

    if (isVisibleToPartner) {
      return related?.provider?.locale?.toLocaleUpperCase() ?? '';
    }

    return '';
  }, [isVisibleToClient, isVisibleToPartner, templateModalData.messengerSourceRelatedObjects]);

  const localeOptions = useMemo(() => {
    return Object.keys(templateMapByConstantName[selectedConstantName] || {}).map((locale) => ({
      value: locale,
      label: locale.toLocaleUpperCase(),
    }));
  }, [selectedConstantName, templateMapByConstantName]);

  const bulkCategoryId = useMemo(
    () =>
      templateModalData.messengerTemplateCategories.find(
        ({ title }) => title === TemplateCategoryEnum.ERP_ACTIVE_TEMPLATES,
      )?.id ?? null,
    [templateModalData.messengerTemplateCategories],
  );

  return {
    category,
    selectedTemplateGroup,
    actionableActions,
    hasActionableMessages,
    currentFormLocale,
    templateMapByConstantName,
    clientOrPartnerLocale,
    localeOptions,
    isEmailSendingAvailable,
    isUploadingFilesAvailable,
    bulkCategoryId,
  };
};
